<template>
  <div class="mall-store">
    <div class="mall-store__shell">
      <div v-if="loading" class="mall-store__empty">
        <AppIcon icon="mdi-loading" :size="40" />
        <p>스토어를 불러오는 중입니다…</p>
      </div>

      <div v-else-if="!store" class="mall-store__empty">
        <AppIcon icon="mdi-store-off-outline" :size="56" />
        <p>판매업체를 찾을 수 없습니다.</p>
        <router-link :to="MALL_HOME_PATH">함우리몰 홈으로</router-link>
      </div>

      <template v-else>
        <header class="mall-store__head">
          <nav class="mall-store__crumb">
            <router-link :to="MALL_HOME_PATH">함우리몰</router-link>
            <AppIcon icon="mdi-chevron-right" :size="16" />
            <span>판매업체</span>
          </nav>
          <h1>{{ store.storeName }}</h1>
          <p class="mall-store__meta">
            <span v-if="shippingLabel">{{ shippingLabel }}</span>
            <span v-if="sameDayCutoffText">{{ sameDayCutoffText }}</span>
            <span v-if="returnFeeLabel">{{ returnFeeLabel }}</span>
          </p>
          <p v-if="store.returnPolicy" class="mall-store__policy">{{ store.returnPolicy }}</p>
        </header>

        <div class="mall-store__toolbar">
          <span>{{ products.length }}개 상품</span>
        </div>

        <div v-if="products.length" class="mall-store__grid">
          <ProductCard v-for="product in products" :key="product.id" :product="product" />
        </div>
        <p v-else class="mall-store__empty-inline">등록된 판매 상품이 없습니다.</p>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import AppIcon from '@/components/common/AppIcon.vue'
import ProductCard from '@/components/mall/ProductCard.vue'
import { fetchMallStore, toMallProduct, type MallPublicStore } from '@/api/mallProduct'
import { MALL_HOME_PATH } from '@/constants/mall'
import { type MallProduct } from '@/constants/mallProducts'
import { summarizeProductShippingFees } from '@/utils/mallShipping'
import { useSameDayCutoffText } from '@/composables/useContentSnippets'

const route = useRoute()
const loading = ref(true)
const store = ref<MallPublicStore | null>(null)
const products = ref<MallProduct[]>([])
const { sameDayCutoffText, loadSameDayCutoffSnippet } = useSameDayCutoffText(
  () => store.value?.cutoff,
)

/** 고객에게는 스토어 기본값이 아니라 실제 판매 상품 배송비를 보여 준다. */
const shippingLabel = computed(() =>
  summarizeProductShippingFees(products.value.map((product) => product.shippingFee)),
)

const returnFeeLabel = computed(() => (store.value ? '반품 배송비는 상품마다 다릅니다' : ''))

async function load() {
  loading.value = true
  store.value = null
  products.value = []

  try {
    const data = await fetchMallStore(String(route.params.code || ''))
    store.value = data.store
    products.value = data.products.map(toMallProduct)
  } catch {
    store.value = null
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void loadSameDayCutoffSnippet()
  void load()
})
watch(
  () => route.params.code,
  () => {
    load()
  },
)
</script>

<style lang="scss" src="./MallStorePage.scss"></style>
